/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import CrossIcon from '@atlaskit/icon/core/cross';
import PageIcon from '@atlaskit/icon/core/page';
import StatusVerifiedIcon from '@atlaskit/icon/core/status-verified';
import Popup from '@atlaskit/popup';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import messages from './messages';

type AuthoritySource = {
	title: string;
	url: string | null;
	authorityScore: number;
	reasoning: string;
};

type AuthorityDomainExpert = {
	name: string;
	identifier: string;
	role: string;
	reasoning: string;
};

/**
 * Which detail panel (if any) is currently expanded. Only one can be open at a time.
 */
type OpenPanel = 'none' | 'sources' | 'experts';

/**
 * Hover-to-open + click-to-lock popup behaviour, matching the inline citation pattern.
 * - Hovering the trigger opens the popup after no delay.
 * - Moving the mouse off the trigger starts a 150ms close timer.
 * - Moving into the popup content cancels the timer (so you can interact with it).
 * - Clicking the trigger locks it open; click again or close button to dismiss.
 */
const useHoverPopup = (isOpen: boolean, open: () => void, close: () => void) => {
	const lockedByClick = useRef(false);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(
		() => () => {
			if (closeTimer.current) clearTimeout(closeTimer.current);
		},
		[],
	);

	// Reset lockedByClick whenever the popup closes (Escape, click-outside, or close button).
	// Without this, hover-close stops working after an external dismiss.
	const closeAndUnlock = useCallback(() => {
		lockedByClick.current = false;
		close();
	}, [close]);

	const onTriggerMouseEnter = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
		open();
	}, [open]);
	const onTriggerMouseLeave = useCallback(() => {
		closeTimer.current = setTimeout(() => {
			if (!lockedByClick.current) closeAndUnlock();
		}, 150);
	}, [closeAndUnlock]);
	const onTriggerClick = useCallback(() => {
		if (isOpen && lockedByClick.current) {
			closeAndUnlock();
			return;
		}
		lockedByClick.current = !lockedByClick.current;
		open();
	}, [isOpen, open, closeAndUnlock]);
	const onContentMouseEnter = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
	}, []);
	const onContentMouseLeave = useCallback(() => {
		if (!lockedByClick.current) closeAndUnlock();
	}, [closeAndUnlock]);
	const onCloseButton = closeAndUnlock;

	return {
		onTriggerMouseEnter,
		onTriggerMouseLeave,
		onTriggerClick,
		onContentMouseEnter,
		onContentMouseLeave,
		onCloseButton,
	};
};

export type AuthoritativeAnswerAttributionProps = {
	/** Trusted sources that contributed to the answer. */
	sources: AuthoritySource[];
	/** Domain experts associated with the answer's topic. */
	domainExperts: AuthorityDomainExpert[];
	/**
	 * Surface variant. `"generated"` (default) is for Chat — renders a standalone
	 * row with icon and "Generated from…" text. `"answer"` is for SAIN — renders
	 * only the attribution text to slot inline after the existing heading in the
	 * AICollapse title row.
	 */
	labelVariant?: 'generated' | 'answer';
	/**
	 * Invoked when a source row is activated. Surfaces inject their own
	 * navigation + analytics. The source's `url` may be `null`.
	 */
	onSourceClick?: (source: AuthoritySource) => void;
	/**
	 * Invoked when an expert row is activated. Surfaces inject their own
	 * navigation + analytics.
	 */
	onExpertClick?: (expert: AuthorityDomainExpert) => void;
	/** Fired when either detail panel is opened, for surface-level analytics. */
	onPanelOpen?: (panel: 'sources' | 'experts') => void;
	/**
	 * Cloud/tenant ID used to construct Atlassian Home profile URLs for expert contributors.
	 * When provided, experts with a valid accountId will show a profile link.
	 */
	cloudId?: string;
	testId?: string;
};

const styles = cssMap({
	root: {
		marginBlockEnd: token('space.100'),
	},
	icon: {
		display: 'inline-flex',
		verticalAlign: 'middle',
		color: token('color.text.success'),
	},
	panelHeadingIcon: {
		color: token('color.text.success'),
		flexShrink: 0,
	},
	link: {
		display: 'inline',
		backgroundColor: 'transparent',
		paddingBlock: token('space.0'),
		paddingInline: token('space.0'),
		color: token('color.link'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		cursor: 'pointer',
		'&:hover': {
			color: token('color.link.pressed'),
			textDecoration: 'underline',
		},
	},
	panel: {
		width: '380px',
		maxWidth: '90vw',
		maxHeight: '60vh',
		overflow: 'auto',
	},
	panelChat: {
		width: '380px',
		maxWidth: '90vw',
		maxHeight: '25vh',
		overflow: 'auto',
	},
	panelHeader: {
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.100'),
		paddingInline: token('space.200'),
	},
	expertRow: {
		display: 'block',
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		backgroundColor: token('color.background.neutral.subtle'),
		textAlign: 'start',
		cursor: 'pointer',
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
	},
	expertRowNoLink: {
		display: 'block',
		width: '100%',
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
		backgroundColor: token('color.background.neutral.subtle'),
		textAlign: 'start',
		cursor: 'default',
	},
	expertRowArrow: {
		flexShrink: 0,
		opacity: 0,
		transition: `opacity ${token('motion.duration.medium')}`,
	},
	expertRowArrowVisible: {
		flexShrink: 0,
		opacity: 1,
		transition: `opacity ${token('motion.duration.medium')}`,
	},
	closeButton: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.xsmall'),
		paddingBlock: token('space.050'),
		paddingInline: token('space.050'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
});

const SourcesPanel = ({
	sources,
	onSourceClick,
	onClose,
	isSAINVariant,
	panelId,
	testId,
}: {
	sources: AuthoritySource[];
	onSourceClick?: (source: AuthoritySource) => void;
	onClose: () => void;
	isSAINVariant: boolean;
	panelId: string;
	testId?: string;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Box id={panelId} xcss={isSAINVariant ? styles.panel : styles.panelChat} testId={testId}>
			<Inline spread="space-between" alignBlock="start" xcss={styles.panelHeader}>
				<Stack space="space.025">
					<Inline alignBlock="center" space="space.075">
						<Box xcss={styles.panelHeadingIcon}>
							<StatusVerifiedIcon label="" size="small" />
						</Box>
						<Heading as="h2" size="xsmall">
							{formatMessage(messages.sourcesPanelHeading, { count: sources.length })}
						</Heading>
					</Inline>
					<Text color="color.text.subtlest" size="small">
						{formatMessage(messages.sourcesPanelDescription)}
					</Text>
				</Stack>
				<Pressable
					xcss={styles.closeButton}
					onClick={onClose}
					aria-label={formatMessage(messages.closePanel)}
					testId={testId ? `${testId}--close` : undefined}
				>
					<CrossIcon label="" size="small" />
				</Pressable>
			</Inline>
			<Stack space="space.0">
				{sources.map((source, index) => {
					const sourceContent = (
						<Stack space="space.050">
							<Inline alignBlock="center" space="space.100">
								<PageIcon label="" size="medium" />
								<Text weight="medium" maxLines={1}>
									{source.title}
								</Text>
							</Inline>
							{source.reasoning ? (
								<Text color="color.text.subtlest" size="small">
									{source.reasoning}
								</Text>
							) : null}
						</Stack>
					);
					return source.url ? (
						<Pressable
							key={`${source.title}-${index}`}
							xcss={styles.expertRow}
							onClick={() => {
								onSourceClick?.(source);
								window.open(source.url!, '_blank', 'noopener,noreferrer');
							}}
							testId={testId ? `${testId}--source-${index}` : undefined}
						>
							{sourceContent}
						</Pressable>
					) : (
						<Box
							key={`${source.title}-${index}`}
							xcss={styles.expertRowNoLink}
							testId={testId ? `${testId}--source-${index}` : undefined}
						>
							{sourceContent}
						</Box>
					);
				})}
			</Stack>
		</Box>
	);
};

const ExpertRow = ({
	expert,
	index,
	onExpertClick,
	cloudId,
	testId,
}: {
	expert: AuthorityDomainExpert;
	index: number;
	onExpertClick?: (expert: AuthorityDomainExpert) => void;
	cloudId?: string;
	testId?: string;
}) => {
	const { formatMessage } = useIntl();
	const [isHovered, setIsHovered] = useState(false);
	// identifier may be an ARI (`ari:cloud:identity::user/{accountId}`),
	// a bare accountId (`712020:abc-123`), or an unresolvable slug (`MCB`).
	// Extract the accountId portion for profile linking.
	const accountId = expert.identifier?.includes('/')
		? expert.identifier.split('/').pop() // ARI — take segment after last /
		: expert.identifier?.includes(':')
			? expert.identifier // bare accountId (contains colon)
			: undefined; // unresolvable slug — skip link
	const profileUrl = accountId
		? cloudId
			? `https://home.atlassian.com/people/${accountId}?cloudId=${cloudId}`
			: `/people/${accountId}`
		: undefined;

	const expertContent = (
		<Inline alignBlock="start" space="space.100">
			<Avatar size="medium" name={expert.name} />
			<Stack space="space.025" grow="fill">
				<Inline alignBlock="center" spread="space-between">
					<Stack space="space.025">
						<Heading as="h3" size="xxsmall">
							{expert.name}
						</Heading>
						{expert.role ? (
							<Text color="color.text.subtlest" size="small">
								{expert.role}
							</Text>
						) : null}
					</Stack>
					{profileUrl ? (
						<Box xcss={isHovered ? styles.expertRowArrowVisible : styles.expertRowArrow}>
							<IconButton
								icon={ArrowRightIcon}
								label={formatMessage(messages.viewExpert, { name: expert.name })}
								appearance="default"
								tabIndex={isHovered ? 0 : -1}
							/>
						</Box>
					) : null}
				</Inline>
				{expert.reasoning ? (
					<Text color="color.text.subtlest" size="small">
						{expert.reasoning}
					</Text>
				) : null}
			</Stack>
		</Inline>
	);

	return profileUrl ? (
		<Pressable
			xcss={styles.expertRow}
			onClick={() => {
				onExpertClick?.(expert);
				window.open(profileUrl, '_blank', 'noopener,noreferrer');
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			testId={testId ? `${testId}--expert-${index}` : undefined}
		>
			{expertContent}
		</Pressable>
	) : (
		<Box xcss={styles.expertRowNoLink} testId={testId ? `${testId}--expert-${index}` : undefined}>
			{expertContent}
		</Box>
	);
};

const ExpertsPanel = ({
	domainExperts,
	onExpertClick,
	onClose,
	isSAINVariant,
	cloudId,
	panelId,
	testId,
}: {
	domainExperts: AuthorityDomainExpert[];
	onExpertClick?: (expert: AuthorityDomainExpert) => void;
	onClose: () => void;
	isSAINVariant: boolean;
	cloudId?: string;
	panelId: string;
	testId?: string;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Box id={panelId} xcss={isSAINVariant ? styles.panel : styles.panelChat} testId={testId}>
			<Inline spread="space-between" alignBlock="start" xcss={styles.panelHeader}>
				<Stack space="space.025">
					<Inline alignBlock="center" space="space.075">
						<Box xcss={styles.panelHeadingIcon}>
							<StatusVerifiedIcon label="" size="small" />
						</Box>
						<Heading as="h2" size="xsmall">
							{formatMessage(messages.expertsPanelHeading, { count: domainExperts.length })}
						</Heading>
					</Inline>
					<Text color="color.text.subtlest" size="small">
						{formatMessage(messages.expertsPanelDescription)}
					</Text>
				</Stack>
				<Pressable
					xcss={styles.closeButton}
					onClick={onClose}
					aria-label={formatMessage(messages.closePanel)}
					testId={testId ? `${testId}--close` : undefined}
				>
					<CrossIcon label="" size="small" />
				</Pressable>
			</Inline>
			<Stack space="space.0">
				{domainExperts.map((expert, index) => (
					<ExpertRow
						key={`${expert.identifier}-${index}`}
						expert={expert}
						index={index}
						onExpertClick={onExpertClick}
						cloudId={cloudId}
						testId={testId}
					/>
				))}
			</Stack>
		</Box>
	);
};

/**
 * Pure, stateless attribution line shown above an authoritative AI answer.
 *
 * Renders "Generated from trusted [sources] and [expert contributors]." where
 * each link opens a dismissible detail panel. Owns presentation only — all
 * navigation and analytics behaviour is injected by the consuming surface via
 * the `onSourceClick` / `onExpertClick` / `onPanelOpen` callbacks.
 *
 * Designed for reuse across Rovo surfaces (Rovo Chat, SAIN).
 */
export const AuthoritativeAnswerAttribution = ({
	sources,
	domainExperts,
	labelVariant = 'generated',
	onSourceClick,
	onExpertClick,
	onPanelOpen,
	cloudId,
	testId,
}: AuthoritativeAnswerAttributionProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const instanceId = useId();
	const sourcesPanelId = `${instanceId}-sources-panel`;
	const expertsPanelId = `${instanceId}-experts-panel`;
	const [openPanel, setOpenPanel] = useState<OpenPanel>('none');

	const hasSources = sources.length > 0;
	const hasExperts = domainExperts.length > 0;

	const openSources = useCallback(() => {
		setOpenPanel('sources');
		onPanelOpen?.('sources');
	}, [onPanelOpen]);
	const openExperts = useCallback(() => {
		setOpenPanel('experts');
		onPanelOpen?.('experts');
	}, [onPanelOpen]);
	const closePanel = useCallback(() => setOpenPanel('none'), []);

	const isSAINVariant = labelVariant === 'answer';
	const sourcesHover = useHoverPopup(openPanel === 'sources', openSources, closePanel);
	const expertsHover = useHoverPopup(openPanel === 'experts', openExperts, closePanel);

	if (!hasSources && !hasExperts) {
		return null;
	}

	const sourcesLink = (
		<Popup
			isOpen={openPanel === 'sources'}
			onClose={sourcesHover.onCloseButton}
			placement="bottom-start"
			content={() => (
				<Box
					onMouseEnter={sourcesHover.onContentMouseEnter}
					onMouseLeave={sourcesHover.onContentMouseLeave}
				>
					<SourcesPanel
						sources={sources}
						onSourceClick={onSourceClick}
						onClose={sourcesHover.onCloseButton}
						isSAINVariant={isSAINVariant}
						panelId={sourcesPanelId}
						testId={testId ? `${testId}--sources-panel` : undefined}
					/>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Box
					as="span"
					role="button"
					tabIndex={0}
					{...triggerProps}
					aria-expanded={openPanel === 'sources'}
					aria-controls={sourcesPanelId}
					xcss={styles.link}
					onClick={sourcesHover.onTriggerClick}
					onKeyDown={(e: React.KeyboardEvent) => {
						if (e.key === 'Enter' || e.key === ' ') sourcesHover.onTriggerClick();
					}}
					onMouseEnter={isSAINVariant ? sourcesHover.onTriggerMouseEnter : undefined}
					onMouseLeave={isSAINVariant ? sourcesHover.onTriggerMouseLeave : undefined}
					testId={testId ? `${testId}--sources-link` : undefined}
				>
					{formatMessage(messages.sourcesLink)}
				</Box>
			)}
		/>
	);

	const expertsLink = (
		<Popup
			isOpen={openPanel === 'experts'}
			onClose={expertsHover.onCloseButton}
			placement="bottom-start"
			content={() => (
				<Box
					onMouseEnter={expertsHover.onContentMouseEnter}
					onMouseLeave={expertsHover.onContentMouseLeave}
				>
					<ExpertsPanel
						domainExperts={domainExperts}
						onExpertClick={onExpertClick}
						onClose={expertsHover.onCloseButton}
						isSAINVariant={isSAINVariant}
						cloudId={cloudId}
						panelId={expertsPanelId}
						testId={testId ? `${testId}--experts-panel` : undefined}
					/>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Box
					as="span"
					role="button"
					tabIndex={0}
					{...triggerProps}
					aria-expanded={openPanel === 'experts'}
					aria-controls={expertsPanelId}
					xcss={styles.link}
					onClick={expertsHover.onTriggerClick}
					onKeyDown={(e: React.KeyboardEvent) => {
						if (e.key === 'Enter' || e.key === ' ') expertsHover.onTriggerClick();
					}}
					onMouseEnter={isSAINVariant ? expertsHover.onTriggerMouseEnter : undefined}
					onMouseLeave={isSAINVariant ? expertsHover.onTriggerMouseLeave : undefined}
					testId={testId ? `${testId}--experts-link` : undefined}
				>
					{formatMessage(messages.expertContributorsLink)}
				</Box>
			)}
		/>
	);

	let line: JSX.Element;
	if (hasSources && hasExperts) {
		line = (
			<FormattedMessage
				{...(isSAINVariant
					? messages.attributionAnswerSourcesAndExperts
					: messages.attributionSourcesAndExperts)}
				values={{ sources: sourcesLink, expertContributors: expertsLink }}
			/>
		);
	} else if (hasSources) {
		line = (
			<FormattedMessage
				{...(isSAINVariant
					? messages.attributionAnswerSourcesOnly
					: messages.attributionSourcesOnly)}
				values={{ sources: sourcesLink }}
			/>
		);
	} else {
		line = (
			<FormattedMessage
				{...(isSAINVariant
					? messages.attributionAnswerExpertsOnly
					: messages.attributionExpertsOnly)}
				values={{ expertContributors: expertsLink }}
			/>
		);
	}

	// SAIN: render only the attribution text — the icon and "Answer" heading are
	// owned by smart-answer-card-consolidated so the title row stays structurally
	// identical to the non-authoritative "Overview" row.
	if (isSAINVariant) {
		return (
			<Text color="color.text.subtlest" testId={testId}>
				{line}
			</Text>
		);
	}

	return (
		<Box xcss={styles.root} testId={testId}>
			<Text color="color.text.subtlest">
				<Box as="span" xcss={styles.icon}>
					<StatusVerifiedIcon label="" size="small" />
				</Box>{' '}
				{line}
			</Text>
		</Box>
	);
};
